import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { nationalTaxServiceCredentialsDialogVar } from '../apollo/reactive-variables/alerts';
import { isInvoicesFirstLoadVar } from '../apollo/reactive-variables/invoices';
import useSelectedCompany from './useSelectedCompany';
import useSnackBars from './useSnackBars';
import useUploadCredentials from './useUploadCredentials';
import useGetCountryFromUrl from './useGetCountryFromUrl';
import { VALIDATORS, SETTERS } from '../helpers/national-identifier';
import { GTM_EVENTS } from '../constants/tag-manager-events';
import { pushConversionEvent } from '../helpers/tag-manager';

const ACCOUNT_BLOCKED = 'Account temporarily blocked due to several incorrect attempts to login';
const INVALID_CREDENTIALS = 'invalid_credentials';

const useNationalTaxServiceCredentials = ({
  autocomplete = false,
  source = null,
  onCompleted = () => {},
  successMessage = 'Credenciales ingresadas con éxito',
  closeDialogOnSuccess = true,
  showEmail = false,
  signTyc = true,
} = {}) => {
  const { addAlert } = useSnackBars();
  const countryFromUrl = useGetCountryFromUrl();
  const company = useSelectedCompany();
  const { uploadCredentials } = useUploadCredentials(source);
  const { t } = useTranslation();

  const initialNationalIdentifier = useMemo(
    () => company?.masterEntity?.displayNationalIdentifier || '',
    [company],
  );

  const country = useMemo(
    () => company?.masterEntity?.country || countryFromUrl,
    [company, countryFromUrl],
  );

  const validationSchema = useMemo(() => {
    const identifierValidation = Yup.string()
      .required(`El ${t('National identifier')} es obligatorio`)
      .test(
        'is-valid-identifier',
        `El ${t('National identifier')} no es válido`,
        (value) => {
          const validator = VALIDATORS[country?.name];
          if (validator) {
            return validator(value);
          }
          return true;
        },
      );

    const schema = {
      nationalIdentifier: identifierValidation,
      password: Yup.string().required(t(`La ${t('Credential Password')} es obligatoria`)),
    };
    if (showEmail) {
      schema.email = Yup.string().email('Correo no válido').required('El correo es obligatorio');
    }
    return Yup.object(schema);
  }, [country, t, showEmail]);

  const formik = useFormik({
    initialValues: {
      nationalIdentifier: autocomplete ? initialNationalIdentifier : '',
      password: '',
      ...(showEmail && { email: '' }),
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      const setter = SETTERS[country?.name];
      const companyNationalIdentifier = setter ? setter(values.nationalIdentifier)
        : values.nationalIdentifier;

      uploadCredentials({
        variables: {
          username: companyNationalIdentifier,
          password: values.password,
          companyId: autocomplete ? company?.id : null,
          ...(showEmail && { email: values.email }),
          signTyc,
        },
        onCompleted: () => {
          addAlert({
            id: 'upload-credentials-success',
            message: successMessage,
            color: 'success',
          });
          resetForm();
          onCompleted(values);
          pushConversionEvent({
            event: GTM_EVENTS.CREDENTIAL_UPLOAD,
            nationalIdentifier: companyNationalIdentifier,
            countryName: country?.name,
          });
          if (closeDialogOnSuccess) {
            nationalTaxServiceCredentialsDialogVar({
              open: false,
              autocomplete: false,
            });
          }
          isInvoicesFirstLoadVar(true);
          setSubmitting(false);
        },
        onError: ({ message }) => {
          let snackbarMessage;
          if (message === INVALID_CREDENTIALS) {
            snackbarMessage = `El ${t(
              'National identifier',
            )} o la ${t('Credential Password')} son incorrectos. Por favor, verifica tus datos e inténtalo de nuevo`;
          } else if (message === ACCOUNT_BLOCKED) {
            snackbarMessage = 'Varios intentos incorrectos de inicio de sesión. Intenta de nuevo más tarde.';
          } else {
            snackbarMessage = 'Hubo un error al verificar tu empresa. Contáctate con nosotros en contacto@fingo.cl';
          }
          addAlert({
            id: 'upload-credentials-error',
            message: snackbarMessage,
            severity: 'error',
            color: 'error',
          });
          setSubmitting(false);
        },
      });
    },
  });

  return {
    formik,
    initialNationalIdentifier,
    country,
    t,
  };
};

export default useNationalTaxServiceCredentials;
